import React, { useEffect, useState } from 'react'
import { useNotificationTemplates } from '@/hooks/useNotificationTemplates'
import { NotificationTemplate } from '@shared/types/notificationTemplate'
import { Mail, Plus, Eye, Check, FileText, AlertCircle } from 'lucide-react'
import { NotificationTemplateEditor } from '@/components/settings/NotificationTemplateEditor'
import { TemplatePreviewModal } from '@/components/settings/TemplatePreviewModal'
import { useToast } from '@/hooks/useToast'

const NotificationTemplatesPage: React.FC = () => {
  const {
    templates,
    templateGroups,
    activeTemplates,
    templateMetadata,
    loading,
    error,
    fetchAllTemplates,
    activateTemplate: doActivateTemplate,
    setSelectedTemplate
  } = useNotificationTemplates()

  const { toast } = useToast()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null)
  const [previewTemplate, setPreviewTemplate] = useState<NotificationTemplate | null>(null)
  const [newVersionTemplateName, setNewVersionTemplateName] = useState<string | undefined>(undefined)

  useEffect(() => {
    fetchAllTemplates()
  }, [])

  useEffect(() => {
    if (error) {
      toast.error('Error', error)
    }
  }, [error, toast])

  const toggleGroup = (name: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [name]: !prev[name]
    }))
  }

  const handleActivate = async (template: NotificationTemplate) => {
    try {
      await doActivateTemplate(template.id)
      toast.success('Template Activated', `Version ${template.version} is now active`)
    } catch (err) {
      toast.error('Activation Failed', 'Failed to activate template')
    }
  }

  const handleEdit = (template: NotificationTemplate) => {
    setEditingTemplate(template)
    setSelectedTemplate(template)
    setIsEditorOpen(true)
  }

  const handleCreateNew = (templateName?: string) => {
    console.log('🆕 handleCreateNew called with templateName:', templateName)

    // If creating a new version of an existing template, get the latest version to copy from
    if (templateName && templateGroups[templateName]) {
      const latestVersion = templateGroups[templateName][0] // Already sorted by version desc
      console.log('📋 Copying from latest version:', latestVersion)
      setEditingTemplate(latestVersion)
      setSelectedTemplate(latestVersion)
    } else {
      setEditingTemplate(null)
      setSelectedTemplate(null)
    }

    // Pass template name as-is (it's already in the correct case from the database)
    setNewVersionTemplateName(templateName)
    setIsEditorOpen(true)
  }

  const handlePreview = (template: NotificationTemplate) => {
    setPreviewTemplate(template)
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getDisplayName = (name: string) => {
    return templateMetadata?.[name]?.displayName || name
  }

  const getDescription = (name: string) => {
    return templateMetadata?.[name]?.description || ''
  }

  if (loading && Object.keys(templateGroups).length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading templates...</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Mail className="h-8 w-8" />
              Email Notification Templates
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage email templates for work authorization notifications
            </p>
          </div>
          <button
            onClick={() => handleCreateNew()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create New Template
          </button>
        </div>
      </div>

      {Object.keys(templateGroups).length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Templates Found</h3>
          <p className="text-muted-foreground mb-4">
            Get started by creating your first notification template
          </p>
          <button
            onClick={() => handleCreateNew()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Create Template
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(templateGroups).map(([name, versions]) => {
            const isExpanded = expandedGroups[name]
            const activeVersion = activeTemplates[name]

            return (
              <div key={name} className="bg-card border border-border rounded-lg overflow-hidden">
                {/* Template Group Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleGroup(name)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {getDisplayName(name)}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {getDescription(name)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {activeVersion && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-md flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            v{activeVersion.version} Active
                          </span>
                        </div>
                      )}
                      {!activeVersion && (
                        <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-500">
                          <AlertCircle className="h-4 w-4" />
                          No active version
                        </div>
                      )}
                      <span className="text-sm text-muted-foreground">
                        {versions.length} version{versions.length !== 1 ? 's' : ''}
                      </span>
                      <svg
                        className={`h-5 w-5 text-muted-foreground transition-transform ${
                          isExpanded ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Template Versions List */}
                {isExpanded && (
                  <div className="border-t border-border">
                    <div className="p-4 space-y-2">
                      <div className="flex justify-end mb-2">
                        <button
                          onClick={() => handleCreateNew(name)}
                          className="px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 rounded-md flex items-center gap-2"
                        >
                          <Plus className="h-3 w-3" />
                          New Version
                        </button>
                      </div>
                      {versions.map((template) => (
                        <div
                          key={template.id}
                          className={`p-4 rounded-lg border ${
                            template.isActive
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/10'
                              : 'border-border bg-background'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold text-foreground">
                                  Version {template.version}
                                </span>
                                {template.isActive && (
                                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                                    Active
                                  </span>
                                )}
                              </div>
                              <div className="space-y-1 text-sm text-muted-foreground">
                                <div>
                                  <strong>Subject:</strong> {template.subject}
                                </div>
                                {template.description && (
                                  <div>
                                    <strong>Description:</strong> {template.description}
                                  </div>
                                )}
                                <div>
                                  <strong>Created:</strong> {formatDate(template.createdAt)} by{' '}
                                  {((template as any).creator as any)?.email || 'Unknown'}
                                </div>
                                <div>
                                  <strong>Variables:</strong>{' '}
                                  {Array.isArray(template.variables)
                                    ? template.variables.join(', ')
                                    : 'None'}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handlePreview(template)}
                                className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted transition-colors flex items-center gap-1"
                              >
                                <Eye className="h-3 w-3" />
                                Preview
                              </button>
                              {!template.isActive && (
                                <button
                                  onClick={() => handleActivate(template)}
                                  className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors flex items-center gap-1"
                                >
                                  <Check className="h-3 w-3" />
                                  Activate
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Editor Modal */}
      {isEditorOpen && (
        <NotificationTemplateEditor
          isOpen={isEditorOpen}
          onClose={() => {
            setIsEditorOpen(false)
            setEditingTemplate(null)
            setNewVersionTemplateName(undefined)
          }}
          template={editingTemplate}
          initialTemplateName={newVersionTemplateName}
          onSuccess={() => {
            setIsEditorOpen(false)
            setEditingTemplate(null)
            setNewVersionTemplateName(undefined)
            fetchAllTemplates()
          }}
        />
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          isOpen={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          template={previewTemplate}
        />
      )}
    </div>
  )
}

export default NotificationTemplatesPage
